import { getState, setState, subscribe } from './app/store'

/**
 * Chatango chat window, toggled from the "chat" nav entry.
 *
 * The third-party script only loads the first time the chat is opened. The
 * window can be dragged by its bar; the position is kept for the session. It
 * sits outside #swup, so an open chat survives page visits.
 *
 * Opening moves the focus to the window's close button, closing brings it back
 * to the toggle, so keyboard users neither have to tab past the whole page nor
 * lose their place on <body>.
 */
const POSITION_KEY = 'danino:chat-position'

// Embed styles that move the chat out of the window. On a phone ("Mobile" user
// agent) fwtickm makes emb.js render a full-width ticker iframe fixed to the
// bottom of <body>, over the footer bar and beyond the reach of the window's
// close button and the nav overlay; pos and cv collapse it into a floating
// button the same way. The window is the only place the chat may render.
const DETACHING_STYLES = ['fwtickm', 'pos', 'cv']

let chatWindow = null
let loaded = false

function embedConfig() {
  const raw = chatWindow.dataset.chatConfig ?? '{}'
  try {
    const config = JSON.parse(raw)
    const styles = config.styles ?? {}
    DETACHING_STYLES.forEach((key) => delete styles[key])
    return JSON.stringify(config)
  } catch {
    // emb.js reports a broken config itself.
    return raw
  }
}

function loadChatango() {
  if (loaded) return
  loaded = true

  // emb.js finds itself by this id and reads its config from the script body;
  // the inline style sets the size of the iframe it creates. At 100% the iframe
  // fills [data-chat-body], which the CSS fits between the header and footer
  // bars (60_overlays.sass). Both must be percentages: emb.js only leaves out
  // its "px" suffix when the width is one.
  const script = document.createElement('script')
  script.id = chatWindow.dataset.chatId
  script.async = true
  script.src = chatWindow.dataset.chatSrc
  script.dataset.cfasync = 'false'
  script.style.width = '100%'
  script.style.height = '100%'
  script.textContent = embedConfig()
  chatWindow.querySelector('[data-chat-body]')?.append(script)
}

function clamp(left, top) {
  const maxLeft = Math.max(0, window.innerWidth - chatWindow.offsetWidth)
  const maxTop = Math.max(0, window.innerHeight - chatWindow.offsetHeight)
  return [Math.min(Math.max(0, left), maxLeft), Math.min(Math.max(0, top), maxTop)]
}

function place(left, top) {
  const [x, y] = clamp(left, top)
  chatWindow.style.left = `${x}px`
  chatWindow.style.top = `${y}px`
  chatWindow.style.right = 'auto'
  chatWindow.style.bottom = 'auto'
  return [x, y]
}

function restorePosition() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(POSITION_KEY) || 'null')
    if (Array.isArray(saved)) place(saved[0], saved[1])
  } catch {
    // No saved position — the CSS default applies.
  }
}

function enableDragging() {
  const handle = chatWindow.querySelector('[data-chat-drag]')
  if (!handle) return

  handle.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 || event.target.closest('button')) return
    event.preventDefault()

    const rect = chatWindow.getBoundingClientRect()
    const offsetX = event.clientX - rect.left
    const offsetY = event.clientY - rect.top
    handle.setPointerCapture(event.pointerId)
    // The iframe would swallow pointer events while the cursor is over it.
    chatWindow.classList.add('is-dragging')

    const move = (moveEvent) => place(moveEvent.clientX - offsetX, moveEvent.clientY - offsetY)
    const stop = () => {
      chatWindow.classList.remove('is-dragging')
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', stop)
      handle.removeEventListener('pointercancel', stop)
      const { left, top } = chatWindow.getBoundingClientRect()
      try {
        sessionStorage.setItem(POSITION_KEY, JSON.stringify([left, top]))
      } catch {
        // Position just isn't remembered.
      }
    }

    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', stop)
    handle.addEventListener('pointercancel', stop)
  })

  // The window shrinks with the viewport (CSS), so a dragged one is clamped
  // again. A closed one has no box to measure — reopening restores it.
  window.addEventListener('resize', () => {
    if (chatWindow.hidden || !chatWindow.style.left) return
    const { left, top } = chatWindow.getBoundingClientRect()
    place(left, top)
  })
}

// Back to the first toggle that can take focus. On phones the chat toggle sits
// in the closed (hidden) nav overlay, so the overlay's own toggle comes first;
// on larger screens that one is display: none and the header entry takes it.
function returnFocus() {
  const targets = [
    document.querySelector('[data-nav-toggle]'),
    ...document.querySelectorAll('[data-chat-toggle]'),
  ]
  for (const target of targets) {
    target?.focus()
    if (target && document.activeElement === target) return
  }
}

function render(open) {
  // Hiding the window drops the focus inside it on <body> — check first.
  const focusInside = chatWindow.contains(document.activeElement)
  chatWindow.hidden = !open
  document.querySelectorAll('[data-chat-toggle]').forEach((toggle) => {
    toggle.setAttribute('aria-expanded', String(open))
  })
  if (open) {
    loadChatango()
    restorePosition()
    chatWindow.querySelector('[data-chat-close]')?.focus()
  } else if (focusInside || document.activeElement === document.body) {
    returnFocus()
  }
}

export function startChat() {
  chatWindow = document.querySelector('[data-chat-window]')
  if (!chatWindow) return

  enableDragging()

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-chat-toggle]')) {
      setState({ chatOpen: !getState().chatOpen })
    } else if (event.target.closest('[data-chat-close]')) {
      setState({ chatOpen: false })
    }
  })

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !getState().chatOpen) return
    // An Escape that closes the nav overlay (js/mobileNav.js) doesn't also
    // close the chat the overlay was hiding.
    if (event.defaultPrevented || getState().navOpen) return
    setState({ chatOpen: false })
  })

  subscribe((state, changed) => {
    if (changed.includes('chatOpen')) render(state.chatOpen)
  })
}
